import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fidelizacionApi } from '../api/fidelizacion.api'
import { parseNumeric } from '../components/charts/palette'
import type {
  ConfiguracionFidelizacionCreateInput,
  ConfiguracionFidelizacionUpdateInput,
  ExportFormato,
  ExportResult,
  ExportTipo,
  FiltrosListadoBase,
  FiltrosRango,
  RecursoListado,
  SerieTemporalGranularidad,
  SerieTemporalMetrica,
} from '../types'

const KEYS = {
  all: ['reportes-fidelizacion'] as const,
  configs: () => [...KEYS.all, 'configs'] as const,
  config: (id: number) => [...KEYS.all, 'config', id] as const,
  grupos: () => [...KEYS.all, 'grupos'] as const,
  metadata: (id: number) => [...KEYS.all, 'metadata', id] as const,
  walletBalance: (id: number) => [...KEYS.all, 'wallet-balance', id] as const,
  resumen: (id: number, params: object) => [...KEYS.all, 'resumen', id, params] as const,
  listado: (id: number, recurso: string, params: object) =>
    [...KEYS.all, 'listado', id, recurso, params] as const,
  metrica: (id: number, kind: string, params: object) =>
    [...KEYS.all, 'metrica', id, kind, params] as const,
}

// ---- Configuraciones ----

export function useConfiguracionesFidelizacion(params?: { grupo_id?: number; activo?: boolean }) {
  return useQuery({
    queryKey: [...KEYS.configs(), params],
    queryFn: () => fidelizacionApi.listConfiguraciones(params),
  })
}

export function useConfiguracionFidelizacion(id: number | null) {
  return useQuery({
    queryKey: KEYS.config(id ?? 0),
    queryFn: () => fidelizacionApi.getConfiguracion(id!),
    enabled: typeof id === 'number' && id > 0,
  })
}

export function useGruposDisponiblesFidelizacion() {
  return useQuery({
    queryKey: KEYS.grupos(),
    queryFn: () => fidelizacionApi.getGruposDisponibles(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateConfiguracionFidelizacion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ConfiguracionFidelizacionCreateInput) =>
      fidelizacionApi.createConfiguracion(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.configs() })
    },
  })
}

export function useUpdateConfiguracionFidelizacion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ConfiguracionFidelizacionUpdateInput }) =>
      fidelizacionApi.updateConfiguracion(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.configs() })
      qc.invalidateQueries({ queryKey: KEYS.config(vars.id) })
    },
  })
}

export function useDeleteConfiguracionFidelizacion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => fidelizacionApi.deleteConfiguracion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.configs() })
    },
  })
}

export function useTestConnectionFidelizacion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => fidelizacionApi.testConnection(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: KEYS.configs() })
      qc.invalidateQueries({ queryKey: KEYS.config(id) })
    },
  })
}

// ---- Proxy ----

export function useMetadataFidelizacion(configId: number | null) {
  return useQuery({
    queryKey: KEYS.metadata(configId ?? 0),
    queryFn: () => fidelizacionApi.getMetadata(configId!),
    enabled: typeof configId === 'number' && configId > 0,
    staleTime: 5 * 60 * 1000, // alineado con cache server-side
  })
}

export function useWalletBalance(configId: number | null) {
  return useQuery({
    queryKey: KEYS.walletBalance(configId ?? 0),
    queryFn: () => fidelizacionApi.getWalletBalance(configId!),
    enabled: typeof configId === 'number' && configId > 0,
    staleTime: 60 * 1000,
  })
}

export function useResumenFidelizacion(
  configId: number | null,
  params?: FiltrosRango,
) {
  return useQuery({
    queryKey: KEYS.resumen(configId ?? 0, params || {}),
    queryFn: () => fidelizacionApi.getMetricasResumen(configId!, params),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000, // server cachea 5-15 min; cliente 1 min para evitar refetch al navegar
  })
}

// ---- Listados (Fase 2) ----

/**
 * Obtiene TODOS los participantes paginando internamente (la API tope page_size=200).
 * Sirve principalmente para resolver `participante_id → nombre` en otros listados
 * que sólo entregan el id (ej. canjes).
 */
export function useTodosParticipantesFidelizacion(configId: number | null) {
  return useQuery({
    queryKey: [...KEYS.all, 'todos-participantes', configId ?? 0] as const,
    queryFn: async () => {
      const all: Array<Record<string, unknown>> = []
      const PAGE_SIZE = 200
      const MAX_PAGES = 20 // seguro contra loop infinito
      let page = 1
      while (page <= MAX_PAGES) {
        const res = await fidelizacionApi.getListado(configId!, 'participantes', {
          page,
          page_size: PAGE_SIZE,
        })
        all.push(...res.items)
        if (!res.has_next) break
        page++
      }
      return all
    },
    enabled: typeof configId === 'number' && configId > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Trae TODAS las facturas del periodo paginando internamente (page_size=200).
 * Cada item incluye su arreglo `productos` (líneas por medicamento), lo que
 * permite agregar métricas por producto client-side (por provincia, cadena,
 * representante y día) cuando el upstream no ofrece ese desglose. Pesado: solo
 * debe habilitarse (`enabled`) cuando realmente se necesita (ej. filtro por
 * producto activo).
 */
export function useTodasFacturasFidelizacion(
  configId: number | null,
  filtros: FiltrosListadoBase = {},
  enabled = true,
) {
  const cleanFiltros: FiltrosListadoBase = {}
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== '' && v !== null) {
      cleanFiltros[k] = v as string | number | boolean
    }
  }
  return useQuery({
    queryKey: [...KEYS.all, 'todas-facturas', configId ?? 0, cleanFiltros] as const,
    queryFn: async () => {
      const all: Array<Record<string, unknown>> = []
      const PAGE_SIZE = 200
      const MAX_PAGES = 100 // tope de seguridad (20k facturas)
      let page = 1
      while (page <= MAX_PAGES) {
        const res = await fidelizacionApi.getListado(configId!, 'facturas', {
          ...cleanFiltros,
          page,
          page_size: PAGE_SIZE,
        })
        all.push(...res.items)
        if (!res.has_next) break
        page++
      }
      return all
    },
    enabled: enabled && typeof configId === 'number' && configId > 0,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  })
}

/**
 * Trae TODOS los canjes paginando internamente (page_size=200).
 * Usado para calcular participantes únicos que canjearon por representante.
 */
export function useTodasCanjesFidelizacion(configId: number | null) {
  return useQuery({
    queryKey: [...KEYS.all, 'todas-canjes', configId ?? 0] as const,
    queryFn: async () => {
      const all: Array<Record<string, unknown>> = []
      const PAGE_SIZE = 200
      const MAX_PAGES = 20
      let page = 1
      while (page <= MAX_PAGES) {
        const res = await fidelizacionApi.getListado(configId!, 'canjes', {
          page,
          page_size: PAGE_SIZE,
        })
        all.push(...res.items)
        if (!res.has_next) break
        page++
      }
      return all
    },
    enabled: typeof configId === 'number' && configId > 0,
    staleTime: 5 * 60 * 1000,
  })
}

export function useListadoFidelizacion(
  configId: number | null,
  recurso: RecursoListado,
  filtros: FiltrosListadoBase = {},
  options: { enabled?: boolean } = {},
) {
  // Eliminamos claves undefined antes de armar la queryKey para que
  // cambios en filtros vacíos no invaliden la cache.
  const cleanFiltros: FiltrosListadoBase = {}
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== '' && v !== null) {
      cleanFiltros[k] = v as string | number | boolean
    }
  }
  // `options.enabled` permite desactivar el fetch server-side cuando el consumidor
  // alimenta la tabla con datos client-side (ej. filtro de producto en Ventas).
  const habilitado = options.enabled ?? true
  return useQuery({
    queryKey: KEYS.listado(configId ?? 0, recurso, cleanFiltros),
    queryFn: () => fidelizacionApi.getListado(configId!, recurso, cleanFiltros),
    enabled: habilitado && typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
  })
}

// ---- Métricas (Fase 3) ----

/** Elimina claves con valores undefined/null/'' preservando el tipo de entrada. */
function cleanParams<T extends object>(p?: T): T {
  if (!p) return {} as T
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(p)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v
  }
  return out as T
}

export function useMetricasPorProvincia(
  configId: number | null,
  params?: FiltrosRango & { top?: number },
) {
  const p = cleanParams(params)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'por-provincia', p),
    queryFn: () => fidelizacionApi.getMetricasPorProvincia(configId!, p),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
  })
}

export function useMetricasPorCampo(
  configId: number | null,
  campo: string | null,
  params?: FiltrosRango & { top?: number },
) {
  const p = cleanParams(params) as Record<string, string | number | undefined>
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, `por/${campo ?? ''}`, p),
    queryFn: () => fidelizacionApi.getMetricasPorCampo(configId!, campo!, p),
    enabled: typeof configId === 'number' && configId > 0 && !!campo,
    placeholderData: keepPreviousData,
  })
}

export function useMetricasPorProducto(
  configId: number | null,
  params?: FiltrosRango & { top?: number },
) {
  const p = cleanParams(params)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'por-producto', p),
    queryFn: () => fidelizacionApi.getMetricasPorProducto(configId!, p),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
  })
}

export function useMetricasVentasPorTipo(
  configId: number | null,
  params?: FiltrosRango & { top?: number },
) {
  const p = cleanParams(params)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'ventas-por-tipo', p),
    queryFn: () => fidelizacionApi.getMetricasVentasPorTipo(configId!, p),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
  })
}

export function useMetricasValidacion(configId: number | null, params?: FiltrosRango) {
  const p = cleanParams(params)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'validacion', p),
    queryFn: () => fidelizacionApi.getMetricasValidacion(configId!, p),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })
}

export function useSerieTemporal(
  configId: number | null,
  params: {
    granularidad?: SerieTemporalGranularidad
    metrica?: SerieTemporalMetrica
    desde?: string
    hasta?: string
    provincia?: string
    [extra: string]: string | number | undefined
  },
) {
  const p = cleanParams(params)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'serie-temporal', p),
    queryFn: () => fidelizacionApi.getSerieTemporal(configId!, p),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
  })
}

/**
 * Variación porcentual entre los dos últimos puntos de una serie temporal
 * (último período vs. el inmediatamente anterior). Devuelve `null` si hay menos
 * de dos puntos o el previo es 0 (no hay base de comparación).
 */
export function variacionSerie(
  serie?: Array<{ periodo: string; valor: number | string }>,
): number | null {
  if (!serie || serie.length < 2) return null
  const last = parseNumeric(serie[serie.length - 1].valor)
  const prev = parseNumeric(serie[serie.length - 2].valor)
  if (prev === 0) return null
  return ((last - prev) / prev) * 100
}

/**
 * Variaciones de ventas diaria / semanal / mensual — el "pulso" más reciente,
 * independiente del filtro de fecha de la página. Cada una compara el último
 * período con datos contra el anterior, usando la métrica de ventas del reporte
 * (`monto` o `cajas_eq`). Como la variación es un cociente, el factor de precio
 * de referencia (reportes con lista de precios local) se cancela y el % es
 * correcto sin valorar la serie.
 */
export function useVentasVariaciones(
  configId: number | null,
  metrica: SerieTemporalMetrica,
) {
  const dia = useSerieTemporal(configId, { granularidad: 'dia', metrica })
  const semana = useSerieTemporal(configId, { granularidad: 'semana', metrica })
  const mes = useSerieTemporal(configId, { granularidad: 'mes', metrica })
  return {
    isLoading: dia.isLoading || semana.isLoading || mes.isLoading,
    dia: variacionSerie(dia.data?.serie),
    semana: variacionSerie(semana.data?.serie),
    mes: variacionSerie(mes.data?.serie),
  }
}

export function useMetricasCanjes(configId: number | null, params?: FiltrosRango) {
  const p = cleanParams(params)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'canjes', p),
    queryFn: () => fidelizacionApi.getMetricasCanjes(configId!, p),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })
}

export function useMetricasComparativa(
  configId: number | null,
  params: {
    periodo_a_desde: string
    periodo_a_hasta: string
    periodo_b_desde: string
    periodo_b_hasta: string
    provincia?: string
  } | null,
) {
  const p = cleanParams(params ?? undefined)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'comparativa', p),
    queryFn: () => fidelizacionApi.getMetricasComparativa(configId!, params!),
    enabled:
      typeof configId === 'number' &&
      configId > 0 &&
      !!params?.periodo_a_desde &&
      !!params?.periodo_a_hasta &&
      !!params?.periodo_b_desde &&
      !!params?.periodo_b_hasta,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })
}

export function useFacturasIngreso(configId: number | null, params?: FiltrosRango) {
  const p = cleanParams(params)
  return useQuery({
    queryKey: KEYS.metrica(configId ?? 0, 'facturas-ingreso', p),
    queryFn: () => fidelizacionApi.getFacturasIngreso(configId!, p),
    enabled: typeof configId === 'number' && configId > 0,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })
}

// ---- Exports (Fase 4) ----

export interface ExportarVariables {
  configId: number
  tipo: ExportTipo
  formato: ExportFormato
  params?: Record<string, string | number | boolean | undefined>
}

export function useExportarFidelizacion() {
  return useMutation<ExportResult, unknown, ExportarVariables>({
    mutationFn: ({ configId, tipo, formato, params }) =>
      fidelizacionApi.exportar(configId, tipo, formato, params),
  })
}

/** Dispara la descarga de un Blob como archivo en el navegador. */
export function descargarBlob(result: ExportResult): void {
  const url = URL.createObjectURL(result.blob)
  const a = document.createElement('a')
  a.href = url
  a.download = result.filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Liberamos el ObjectURL en el siguiente tick para no cortar la descarga.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export { KEYS as FIDELIZACION_QUERY_KEYS }
